/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { db } from '../core/storage/database'

const AUDIO_CACHE_NAME = 'repet-audio'

/**
 * Nettoie les fichiers audio orphelins du CacheStorage et d'IndexedDB
 * après la suppression d'une ou plusieurs versions.
 *
 * Algorithme :
 * 1. Collecter les content_hash référencés par les compilations des versions supprimées
 * 2. Vérifier si chaque hash est encore référencé par une autre version locale
 * 3. Supprimer du CacheStorage + db.audioCache les entrées orphelines
 * 4. Supprimer les compilations locales des versions supprimées
 *
 * Best-effort : une erreur sur un fichier individuel ne bloque pas les suivants.
 * Une erreur globale est catchée pour ne pas bloquer la suppression de l'entité.
 *
 * @param deletedVersionIds - IDs des versions supprimées localement
 */
/**
 * @param deletedVersionIds  - IDs des versions supprimées
 * @param preCollectedHashes - Map versionId → Set<hash> collectée AVANT la transaction
 *                             de suppression (les compilations sont déjà effacées au
 *                             moment de l'appel). Si absent, fallback sur une requête DB
 *                             (utile pour les appelants externes qui n'ont pas pré-collecté).
 */
export async function cleanOrphanAudio(
  deletedVersionIds: string[],
  preCollectedHashes?: Map<string, Set<string>>
): Promise<void> {
  if (deletedVersionIds.length === 0) return

  try {
    // Étape 1 : Construire l'ensemble des hashes candidats à la suppression.
    // On utilise les hashes pré-collectés si disponibles (cas nominal : compilations
    // déjà effacées par la transaction). Sinon, on interroge db.compilations
    // directement (cas fallback : compilations encore présentes).
    const candidateHashes = new Set<string>()

    if (preCollectedHashes && preCollectedHashes.size > 0) {
      for (const hashes of preCollectedHashes.values()) {
        for (const hash of hashes) candidateHashes.add(hash)
      }
    } else {
      // Fallback : les compilations n'ont pas encore été supprimées
      const compilations = await db.compilations
        .where('textVersionId')
        .anyOf(deletedVersionIds)
        .toArray()
      for (const compilation of compilations) {
        if (!compilation.audioManifest) continue
        try {
          const manifest = JSON.parse(compilation.audioManifest) as {
            lines?: Array<{ contentHash?: string }>
          }
          for (const line of manifest?.lines ?? []) {
            const hash = line.contentHash
            if (hash) candidateHashes.add(hash)
          }
        } catch {
          // manifest malformé — ignorer
        }
      }
    }

    if (candidateHashes.size === 0) return

    // Étape 2 : Pour chaque hash candidat, vérifier s'il reste référencé
    // par une compilation d'une version qui n'est PAS dans deletedVersionIds.
    const orphanHashes: string[] = []
    for (const hash of candidateHashes) {
      const stillReferenced = await _isHashStillReferenced(hash, deletedVersionIds)
      if (!stillReferenced) orphanHashes.push(hash)
    }

    // Étape 3 : Supprimer les entrées audioCache et les fichiers CacheStorage orphelins
    let cacheStorage: Cache | null = null
    if ('caches' in window) {
      try {
        cacheStorage = await caches.open(AUDIO_CACHE_NAME)
      } catch (err) {
        console.warn("[audioCacheService] Impossible d'ouvrir le CacheStorage:", err)
      }
    }

    for (const hash of orphanHashes) {
      try {
        const entry = await db.audioCache.get(hash)
        if (entry && cacheStorage) {
          try {
            await cacheStorage.delete(new Request(entry.filePath))
          } catch (err) {
            console.warn(`[audioCacheService] Échec suppression CacheStorage hash=${hash}:`, err)
          }
        }
        try {
          await db.audioCache.delete(hash)
        } catch (err) {
          console.warn(`[audioCacheService] Échec suppression db.audioCache hash=${hash}:`, err)
        }
      } catch (err) {
        console.warn(`[audioCacheService] Erreur traitement hash=${hash}:`, err)
      }
    }
    // Note : les compilations sont supprimées par la transaction dans textsRepository —
    // pas de db.compilations.delete() ici pour éviter la double suppression.
  } catch (err) {
    console.warn('[audioCacheService] cleanOrphanAudio a échoué (non-bloquant):', err)
  }
}

/**
 * Vérifie si un content_hash est encore référencé par une compilation
 * appartenant à une version qui n'est PAS dans `excludedVersionIds`.
 *
 * @param hash - Hash à vérifier
 * @param excludedVersionIds - IDs de versions à exclure de la vérification
 */
async function _isHashStillReferenced(
  hash: string,
  excludedVersionIds: string[]
): Promise<boolean> {
  try {
    // Chercher des compilations dont l'audioManifest contient ce hash
    // et dont la textVersionId n'est pas dans les versions supprimées
    const remainingCompilations = await db.compilations
      .filter((c) => !excludedVersionIds.includes(c.textVersionId))
      .toArray()

    for (const compilation of remainingCompilations) {
      if (!compilation.audioManifest) continue
      try {
        const manifest = JSON.parse(compilation.audioManifest) as {
          lines?: Array<{ contentHash?: string }>
        }
        const lines = manifest?.lines ?? []
        const found = lines.some((line) => line.contentHash === hash)
        if (found) return true
      } catch {
        // manifest malformé — ignorer
      }
    }

    return false
  } catch {
    // En cas d'erreur, on considère le hash comme référencé (prudence)
    return true
  }
}
